import { AfterViewInit, Component, OnDestroy, OnInit } from '@angular/core';
import { CanDeactivateGuard } from '@guards/can-deactivate/can-deactivate-guard.service';
import { clone } from '@helpers/arrays';
import { DialogSaveObject } from '@interfaces/dialog-save-object';
import { IGlobalParam } from '@interfaces/global-param';
import { GlobalParamsService, MAX_PERIOD } from '@services/global-params/global-params.service';
import { MenuService } from '@services/menu/menu.service';
import { NotificationsService } from '@services/notifications/notifications.service';
import { ToastLevel } from '@shared/toasterSetup';
import { Toast } from 'ngx-toastr';
import { Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import Swal, { SweetAlertOptions } from 'sweetalert2';

@Component({
  selector: 'app-global-settings',
  templateUrl: './global-settings.component.html',
  styleUrls: ['./global-settings.component.scss'],
})
export class GlobalSettingsComponent implements OnInit, AfterViewInit, OnDestroy, CanDeactivateGuard {
  private toast: Toast = { title: '', message: 'Global Settings successfully updated' } as Toast;
  private subscriptions: Subscription[] = [];
  public params: IGlobalParam[] = [];
  public storedParams: IGlobalParam[] = [];
  public isSaving = false;
  public numPeriodsModified = false;

  get areValidParams() {
    return this.params.every(p => p.Valid);
  }

  constructor(private globalParams: GlobalParamsService, private menu: MenuService, public notifications: NotificationsService) {}

  ngOnInit() {
    this.globalParams.getParams().subscribe((params: any[]) => {
      this.params = params;
      this.storedParams = clone(params);
    });
  }

  onParamValueChanged(param: IGlobalParam) {
    param.Modified = true;

    const settingsChanged = this.settingsChanged();
    const dialogSaveButton = {
      page: 'global-settings',
      enabled: settingsChanged && this.areValidParams,
      valid: this.areValidParams,
    };

    this.menu.selectedMenuItemCanDeactivate(!dialogSaveButton.enabled);
    this.menu.dialogSaveButton = dialogSaveButton;
  }

  ngAfterViewInit() {
    // Dialog Save Button changes
    this.subscriptions.push(
      this.menu.dialogSaveButton$.pipe(distinctUntilChanged(), debounceTime(100)).subscribe((val: DialogSaveObject) => {
        // Only respond to click event if was triggered for this page
        if (val.page === 'global-settings' && val.clicked) {
          const numPeriods = parseInt(this.params.find(p => p.ParamName === 'NUM_PERIODS').ParamValue, 10);
          const storedNumPeriods = parseInt(this.storedParams.find(p => p.ParamName === 'NUM_PERIODS').ParamValue, 10);

          if (numPeriods !== storedNumPeriods) {
            this.save()
              .then(result => {
                const saveConfirmed = !!result.value;
                // console.log(`⭐ saveConfirmed: ${saveConfirmed}`);
                this.numPeriodsModified = true;

                if (saveConfirmed) this.saveAll();
              })
              .finally(() => {
                this.menu.dialogSaveButton = {
                  page: `global-settings`,
                  clicked: false,
                  source: `global-settings save`,
                };
              });
          } else {
            // NumPeriods didn't change
            this.saveAll();
          }
        }
      })
    );
  }

  ngOnDestroy() {
    this.notifications.hideToasts();
    this.subscriptions.forEach((subs: Subscription) => subs.unsubscribe());
  }

  private settingsChanged() {
    // If params are different to storedParams
    this.checkValidSettings();
    for (let i = 0; i < this.storedParams.length; i++) {
      const param = this.params[i];
      const storedParam = this.storedParams[i];

      if (!param.ReadOnly) {
        if (param.ParamValue !== storedParam.ParamValue) {
          return true;
        }
      }
    }

    return false;
  }

  private async save() {
    return await Swal.fire({
      title: `Import Required!`,
      // tslint:disable-next-line:max-line-length
      text: `Changing the number of periods will need to reimport data. It will also delete all unsaved lookup table data and period equations changes for all users.`,
      type: 'warning',
      showCancelButton: true,
      confirmButtonText: 'Continue',
      cancelButtonText: 'Cancel',
      reverseButtons: true,
      allowEscapeKey: true,
    } as SweetAlertOptions);
  }

  private saveGlobalSettings() {
    this.isSaving = true;
    const numPeriods = this.params.find(p => p.ParamName === 'NUM_PERIODS');
    const periods = parseInt(numPeriods.ParamValue, 10);
    const horizon = this.params.find(p => p.ParamName === 'SURVIVAL_HORIZON').ParamValue;

    const fileLocatioNField = this.params.find(p => p.ParamName === 'IMPORT_FILE_LOCATION');
    const file_location = fileLocatioNField ? fileLocatioNField.ParamValue : undefined;
    const idle_time = parseInt(this.params.find(p => p.ParamName === 'MAX_IDLE_TIME').ParamValue, 10);

    this.globalParams
      .updateUserSettings(periods, horizon, file_location, idle_time)
      .toPromise()
      .then(response => {
        if (response === true) {
          this.params.forEach(param => {
            param.Modified = false;
            param.Valid = true;
          });
          this.storedParams = clone(this.params);
          this.notifications.showToast(ToastLevel.Success, this.toast);
          // Trigger the related banner message (If is needed):
          if (this.numPeriodsModified) {
            this.notifications.mainNotificationBanner$.next({ action: 'display', name: 'numPeriods' });
          }
        }
      })
      .catch(err => {
        this.notifications.showToast(ToastLevel.Error, { title: '', message: 'Global Settings not saved' } as Toast);
        console.warn(`Error saving Global Settings`, { err });
      })
      .finally(() => {
        this.menu.dialogSaveButton = {
          page: `global-settings`,
          enabled: false,
          valid: this.areValidParams,
          clicked: false,
          source: `global-settings save`,
        };
        this.isSaving = false;
        this.numPeriodsModified = false;
      });
  }

  private saveAll() {
    this.saveGlobalSettings();
    // Save Survival Horizon here
  }

  private checkValidSettings() {
    // NUM_PERIODS
    const numPeriods = this.params.find(p => p.ParamName === 'NUM_PERIODS');
    const value = parseInt(numPeriods.ParamValue, 10);
    numPeriods.Valid = Number(value) === value && 0 < value && value <= MAX_PERIOD;

    const horizon = this.params.find(p => p.ParamName === 'SURVIVAL_HORIZON');
    const survival = parseInt(horizon.ParamValue, 10);
    horizon.Valid = Number(survival) === survival && 0 < survival;

    const idle_time = this.params.find(p => p.ParamName === 'MAX_IDLE_TIME');
    const idle_value = parseInt(idle_time.ParamValue, 10);
    idle_time.Valid = Number(idle_value) === idle_value && idle_value >= 5;
  }

  async canDeactivate(): Promise<boolean> {
    // const message =
    //   'You have unsaved changes on this page. Please click Cancel to return to the page or continue to discard changes';
    return this.menu.canDeactivatePopup();
  }

  public getUnits(param) {
    if (param.ParamName === 'MAX_IDLE_TIME') return 'mins';
    // Uncomment if needed
    // if (param.ParamName === 'NUM_PERIODS') return 'periods';
    // if (param.ParamName === 'SURVIVAL_HORIZON') return 'periods';
  }
}
